import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Check, 
  Shield, 
  CreditCard,
  Loader2,
  Monitor,
  MapPin,
  Sparkles,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';

// Pricing data - must match backend PRODUCTS
const PLANS = {
  online: {
    id: 'online',
    name: 'Online',
    subtitle: '12 clases en Meet',
    icon: Monitor,
    single_price: 1020,
    subscription_price: 270,
    subscription_cycles: 4,
    subscription_total: 1080,
    features: ['Clases en Meet + grabaciones', 'Notion con apuntes y protocolos', 'Obligatoria y previa']
  },
  presencial: {
    id: 'presencial',
    name: 'Presencial',
    subtitle: '3 encuentros en Barcelona',
    icon: MapPin,
    single_price: 555,
    subscription_price: 195,
    subscription_cycles: 3,
    subscription_total: 585,
    features: ['3 fines de semana en Barcelona', 'Requiere: online completada', 'o mínimo 6 clases online']
  },
  pack: {
    id: 'pack',
    name: 'Pack Completo',
    subtitle: 'Online + Presencial',
    icon: Sparkles,
    single_price: 1520,
    subscription_price: 262.50,
    subscription_cycles: 6,
    subscription_total: 1575,
    features: ['Base teórica-energética online', 'Integración vivencial presencial', 'La opción más completa'],
    recommended: true
  }
};

const FacilitadorasCheckout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { slug } = useParams();
  const courseSlug = slug || 'facilitadoras-sosten-energetico';
  
  // URL params for PayPal return
  const paypalStatus = searchParams.get('paypal');
  const paypalType = searchParams.get('type');
  const paypalProduct = searchParams.get('product');
  const subscriptionId = searchParams.get('subscription_id');
  const orderId = searchParams.get('token'); // PayPal sends order ID as 'token'
  const paypalCourseSlug = searchParams.get('course') || courseSlug;
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentProcessed, setPaymentProcessed] = useState(false);
  
  // User selections
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get('plan') || 'pack');
  const [paymentType, setPaymentType] = useState(searchParams.get('payment') || 'single');

  // Handle PayPal return
  useEffect(() => {
    const handlePayPalReturn = async () => {
      if (paypalStatus === 'success' && !paymentProcessed) {
        setProcessing(true);
        setPaymentProcessed(true);
        try {
          if (paypalType === 'single' && orderId) {
            // Capture the order
            const captureRes = await api.post('/paypal/capture-order', { order_id: orderId });
            
            if (captureRes.data.success) {
              // Complete enrollment with course_slug
              const enrollRes = await api.post(`/paypal/complete-enrollment?product_type=${paypalProduct}&payment_type=single&payment_id=${orderId}&course_slug=${paypalCourseSlug}`);
              
              if (enrollRes.data.success) {
                toast.success('¡Pago completado! Bienvenido/a al curso.');
                navigate(enrollRes.data.redirect || `/formaciones/${paypalCourseSlug}/curso`);
                return;
              }
            }
            toast.error('Error al procesar el pago');
          } else if (paypalType === 'subscription' && subscriptionId) {
            // Confirm subscription
            const confirmRes = await api.post('/paypal/confirm-subscription', {
              subscription_id: subscriptionId,
              product_type: paypalProduct
            });
            
            if (confirmRes.data.success) {
              // Complete enrollment with course_slug
              const enrollRes = await api.post(`/paypal/complete-enrollment?product_type=${paypalProduct}&payment_type=subscription&payment_id=${subscriptionId}&course_slug=${paypalCourseSlug}`);
              
              if (enrollRes.data.success) {
                toast.success('¡Suscripción activada! Bienvenido/a al curso.');
                navigate(enrollRes.data.redirect || `/formaciones/${paypalCourseSlug}/curso`);
                return;
              }
            }
            toast.error('Error al activar la suscripción');
          }
        } catch (error) {
          console.error('PayPal return error:', error);
          toast.error(error.response?.data?.detail || 'Error al procesar el pago');
        } finally {
          setProcessing(false);
        }
      } else if (paypalStatus === 'cancelled') {
        toast.info('Pago cancelado');
      }
    };
    
    if (paypalStatus) {
      handlePayPalReturn();
    }
  }, [paypalStatus, paypalType, paypalProduct, orderId, subscriptionId, navigate, paypalCourseSlug, paymentProcessed]);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const courseRes = await api.get(`/special-courses/${courseSlug}`);
        setCourse(courseRes.data);
        
        // Check for existing active enrollment
        try {
          const enrollmentRes = await api.get(`/special-courses/${courseSlug}/enrollment`);
          if (enrollmentRes.data.enrollment?.status === 'active') {
            toast.info('Ya estás inscrito/a en este curso');
            navigate('/mi-camino');
            return;
          }
        } catch (e) {
          // No enrollment - continue
        }
      } catch (error) {
        console.error('Error fetching course:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [navigate, courseSlug]);

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const response = await api.post('/paypal/create-order', {
        product_type: selectedPlan,
        payment_type: paymentType === 'single' ? 'single' : 'subscription',
        course_slug: courseSlug
      });
      
      // Redirect to PayPal
      const approveUrl = response.data.approve_url;
      if (approveUrl) {
        window.location.href = approveUrl;
      } else {
        toast.error('Error al conectar con PayPal');
        setProcessing(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Error al iniciar el pago');
      setProcessing(false);
    }
  };

  if (loading || (paypalStatus === 'success' && processing)) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-[hsl(var(--sage-light))]">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[hsl(var(--text-secondary))]">
            {paypalStatus === 'success' ? 'Procesando tu pago...' : 'Cargando...'}
          </p>
        </div>
      </div>
    );
  }

  if (!course) return null;

  const plan = PLANS[selectedPlan];
  const price = paymentType === 'single' ? plan.single_price : plan.subscription_price;
  const totalPrice = paymentType === 'single' ? plan.single_price : plan.subscription_total;

  return (
    <div className="pt-20 min-h-screen bg-[hsl(var(--sage-light))]">
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12">
        <Link 
          to={`/formaciones/${courseSlug}`} 
          className="inline-flex items-center gap-2 text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Volver al curso
        </Link>

        <h1 className="text-2xl lg:text-3xl font-heading text-[hsl(var(--text-primary))] mb-2">
          Inscripción: {course.title}
        </h1>
        <p className="text-[hsl(var(--text-secondary))] mb-8">
          3ª Edición · Marzo – Junio 2026
        </p>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: Selection */}
          <div className="lg:col-span-2 space-y-8">
            {/* Step 1: Choose Plan */}
            <div className="card-soft">
              <h2 className="text-lg font-heading text-[hsl(var(--text-primary))] mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-[hsl(var(--accent))] text-white text-sm flex items-center justify-center">1</span>
                Elige tu modalidad
              </h2>
              
              <div className="grid gap-3">
                {Object.values(PLANS).map((p) => {
                  const Icon = p.icon;
                  const isSelected = selectedPlan === p.id;
                  
                  return (
                    <button
                      key={p.id}
                      onClick={() => setSelectedPlan(p.id)}
                      className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                        isSelected 
                          ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5' 
                          : 'border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]/50'
                      }`}
                      data-testid={`plan-${p.id}`}
                    >
                      {p.recommended && (
                        <span className="absolute -top-2 right-4 bg-[hsl(var(--accent))] text-white text-xs px-2 py-0.5 rounded-full">
                          Recomendado
                        </span>
                      )}
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-[hsl(var(--accent))]' : 'bg-[hsl(var(--sage-light))]'
                        }`}>
                          <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-[hsl(var(--sage))]'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-[hsl(var(--text-primary))]">{p.name}</span>
                            <span className="text-sm text-[hsl(var(--text-tertiary))]">· {p.subtitle}</span>
                          </div>
                          <div className="mt-1 flex items-baseline gap-2">
                            <span className="text-lg font-semibold text-[hsl(var(--accent))]">
                              {p.single_price.toLocaleString('es-ES')}€
                            </span>
                            <span className="text-sm text-[hsl(var(--text-tertiary))]">
                              pago único
                            </span>
                          </div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]' : 'border-[hsl(var(--border))]'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2: Payment Type */}
            <div className="card-soft">
              <h2 className="text-lg font-heading text-[hsl(var(--text-primary))] mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-[hsl(var(--accent))] text-white text-sm flex items-center justify-center">2</span>
                Forma de pago
              </h2>
              
              <div className="grid gap-3">
                {/* Single Payment */}
                <button
                  onClick={() => setPaymentType('single')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    paymentType === 'single'
                      ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5'
                      : 'border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]/50'
                  }`}
                  data-testid="payment-single"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-[hsl(var(--text-primary))]">Pago único</span>
                      <p className="text-sm text-[hsl(var(--text-tertiary))] mt-1">
                        Un solo pago de {plan.single_price.toLocaleString('es-ES')}€
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentType === 'single' ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]' : 'border-[hsl(var(--border))]'
                    }`}>
                      {paymentType === 'single' && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                </button>

                {/* Subscription Payment */}
                <button
                  onClick={() => setPaymentType('subscription')}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    paymentType === 'subscription'
                      ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5'
                      : 'border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]/50'
                  }`}
                  data-testid="payment-subscription"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-[hsl(var(--text-primary))]">Pago en cuotas</span>
                      <p className="text-sm text-[hsl(var(--text-tertiary))] mt-1">
                        {plan.subscription_cycles} cuotas de {plan.subscription_price.toLocaleString('es-ES')}€/mes
                      </p>
                      <p className="text-xs text-[hsl(var(--text-tertiary))] mt-0.5">
                        Total: {plan.subscription_total.toLocaleString('es-ES')}€
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentType === 'subscription' ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]' : 'border-[hsl(var(--border))]'
                    }`}>
                      {paymentType === 'subscription' && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Step 3: Pay */}
            <div className="card-soft">
              <h2 className="text-lg font-heading text-[hsl(var(--text-primary))] mb-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-[hsl(var(--accent))] text-white text-sm flex items-center justify-center">3</span>
                Pagar con PayPal
              </h2>
              
              <Button
                onClick={handlePayment}
                disabled={processing}
                className="w-full btn-gold py-6 text-lg"
                data-testid="pay-button"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Conectando con PayPal...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    {paymentType === 'single' 
                      ? `Pagar ${price.toLocaleString('es-ES')}€`
                      : `Iniciar suscripción · ${price.toLocaleString('es-ES')}€/mes`
                    }
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              
              <p className="text-center text-sm text-[hsl(var(--text-tertiary))] mt-3">
                Serás redirigido a PayPal para completar el pago de forma segura.
                <br />Puedes pagar con tu cuenta PayPal o con tarjeta.
              </p>
              
              <div className="flex items-center justify-center gap-2 mt-4 text-sm text-[hsl(var(--text-tertiary))]">
                <Shield className="w-4 h-4" />
                Pago seguro con PayPal
              </div>
            </div>
          </div>

          {/* Right: Summary */}
          <div className="lg:col-span-1">
            <div className="card-soft sticky top-24">
              <h3 className="font-heading text-[hsl(var(--text-primary))] mb-4">
                Resumen de tu compra
              </h3>
              
              <div className="space-y-3 pb-4 border-b border-[hsl(var(--border))]">
                <div className="flex justify-between">
                  <span className="text-[hsl(var(--text-secondary))]">Modalidad</span>
                  <span className="font-medium text-[hsl(var(--text-primary))]">{plan.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[hsl(var(--text-secondary))]">Forma de pago</span>
                  <span className="font-medium text-[hsl(var(--text-primary))]">
                    {paymentType === 'single' ? 'Único' : `${plan.subscription_cycles} cuotas`}
                  </span>
                </div>
              </div>
              
              <div className="py-4 border-b border-[hsl(var(--border))]">
                {paymentType === 'single' ? (
                  <div className="flex justify-between items-baseline">
                    <span className="text-[hsl(var(--text-secondary))]">Total</span>
                    <span className="text-2xl font-heading text-[hsl(var(--accent))]">
                      {plan.single_price.toLocaleString('es-ES')}€
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-baseline mb-2">
                      <span className="text-[hsl(var(--text-secondary))]">Hoy pagas</span>
                      <span className="text-2xl font-heading text-[hsl(var(--accent))]">
                        {plan.subscription_price.toLocaleString('es-ES')}€
                      </span>
                    </div>
                    <p className="text-sm text-[hsl(var(--text-tertiary))]">
                      Después: {plan.subscription_cycles - 1} pagos de {plan.subscription_price.toLocaleString('es-ES')}€
                      <br />
                      <span className="text-xs">Total: {plan.subscription_total.toLocaleString('es-ES')}€</span>
                    </p>
                  </>
                )}
              </div>
              
              <div className="pt-4 space-y-2">
                <p className="text-sm font-medium text-[hsl(var(--text-primary))]">Incluye:</p>
                {plan.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-[hsl(var(--text-secondary))]">
                    <Check className="w-4 h-4 text-[hsl(var(--sage))] flex-shrink-0 mt-0.5" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilitadorasCheckout;
